'use client';

import { useState, useEffect, useLayoutEffect, useRef, type CSSProperties } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { fields as castFields } from '@/lib/contentful/cast';
import SmartLink from '@/components/ui/SmartLink';
import { resolveContentfulLink } from '@/lib/links';
import Modal from '@/components/ui/Modal';
import CFImage from '@/components/ui/CFImage';
import RichText from '@/components/ui/RichText';
import { stripEncoding } from '@/lib/bgColor';
import type { HeaderFields, NavigationItemFields, CFTextImageBlock, TextImageBlockFields } from '@/types/contentful';
import type { Entry, EntrySkeletonType } from 'contentful';

// ─── Hardcoded eesy logo SVG ─────────────────────────────────────────────────
function EesyLogo({ forceReload }: { forceReload?: boolean }) {
  const Wrapper = forceReload ? 'a' : Link;
  return (
    <Wrapper href="/" aria-label="eesy – gå til forsiden" className="my-auto block rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-dark">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 600 155.62"
        aria-hidden="true"
        className="block h-[22px] w-[84px] overflow-visible fill-brand-dark lg:h-[25px] lg:w-[96px]"
      >
        <path d="m77.68,155.62C32.67,155.62,0,122.84,0,77.68,0,34.85,33.64,0,75,0s75,34.85,75,77.68c0,16.66-11.97,18.46-17.12,18.46H35.75c6.6,17.1,22.3,27.87,41.93,27.93,16.48.04,23.14-4.97,30.62-10.91,3.94-3.13,7.42-5.93,12.78-5.93,8.7,0,15.78,7.08,15.78,15.78,0,5.35-3.34,9.6-5.52,11.97-6.3,6.82-23.53,20.65-53.65,20.65Zm-43.56-91.04h82.03c-5.39-18.96-22.08-33.02-41.14-33.02s-35.52,14.06-40.88,33.02Z"/>
        <path d="m240.56,155.62c-45.01,0-77.68-32.78-77.68-77.94C162.88,34.85,196.53,0,237.88,0s75,34.85,75,77.68c0,16.66-11.97,18.46-17.12,18.46h-97.13c6.6,17.1,22.3,27.87,41.93,27.93,16.48.04,23.14-4.97,30.62-10.91,3.94-3.13,7.42-5.93,12.78-5.93,8.7,0,15.78,7.08,15.78,15.78,0,5.35-3.34,9.6-5.52,11.97-6.3,6.82-23.53,20.65-53.65,20.65Zm-43.56-91.04h82.03c-5.39-18.96-22.08-33.02-41.14-33.02s-35.52,14.06-40.88,33.02Z"/>
        <path d="m387.21,155.62c-20.94,0-42.44-5.72-58.97-15.7l-.39-.25c-4.35-2.9-7.05-7.97-7.05-13.23,0-8.7,7.08-15.78,15.78-15.78,3.99,0,6.48,1.39,7.68,2.06l1.04.58c11.3,7.03,25.79,10.75,41.92,10.75,3.19,0,14.3-.3,23.53-4.18,9.73-4.09,9.73-9.01,9.73-10.86,0-8.16-14.35-11.59-34.63-15.54l-1.15-.23c-25.65-4.71-60.94-11.19-60.94-46.67,0-32.19,31.88-46.6,63.48-46.6,20.31,0,37.23,5.14,50.3,15.29,3.67,2.8,5.94,7.5,5.94,12.28,0,8.7-7.08,15.78-15.78,15.78-5.25,0-9.23-2.98-10.53-3.96-6.93-5.2-17-7.83-29.93-7.83-8.85,0-17.25,1.62-23.03,4.44-7.16,3.49-8.08,7.5-8.08,10.6,0,1.93,0,4.84,6.77,7.98,6.53,3.02,16.06,4.78,26.15,6.65l1.71.32c24.71,4.49,62.11,11.29,62.11,47.49,0,16.76-9.35,30.42-26.33,38.46-15.51,7.34-32.65,8.14-39.29,8.14Z"/>
        <path d="m592.32,2.31c3.73,2.24,6.22,5.78,7.2,9.7.99,3.91.47,8.21-1.77,11.94l-74.28,123.99c-4.48,7.48-14.17,9.9-21.65,5.42-3.73-2.24-6.22-5.78-7.19-9.7-.99-3.91-.47-8.2,1.77-11.94l12.97-21.66-51.96-86.12c-4.5-7.46-2.11-17.15,5.35-21.66,5.18-3.12,11.43-2.93,16.28-.02,2.14,1.28,4.01,3.09,5.39,5.38l43.3,71.77,42.94-71.68c4.48-7.48,14.17-9.91,21.64-5.43Z"/>
      </svg>
    </Wrapper>
  );
}

const navLinkBarClass =
  'flex items-center px-3 text-base font-normal uppercase tracking-wide text-brand-dark no-underline transition-opacity hover:opacity-70 leading-[22px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-dark focus-visible:ring-inset rounded';

const subnavLinkBaseClass =
  'inline-flex items-center justify-start border-b-[3px] py-2.5 text-[0.9375rem] font-normal uppercase tracking-wide text-brand-dark no-underline transition-[opacity,border-color] duration-150 ease-in-out hover:opacity-70 whitespace-normal leading-5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-dark rounded';

// ─── Nav link (desktop top row vs. subnav bar ≤760px) ─────────────────────────
function NavLink({
  variant = 'bar',
  ...link
}: {
  href: string;
  label: string;
  openInNewTab?: boolean;
  variant?: 'bar' | 'subnav';
}) {
  const { href, label, openInNewTab } = resolveContentfulLink(link);
  const pathname = usePathname();
  const isActive = pathname === href || pathname === `/${href.replace(/^\//, '')}`;

  if (variant === 'subnav') {
    return (
      <li className="flex shrink-0 justify-start px-2">
        <SmartLink
          href={href}
          target={openInNewTab ? '_blank' : undefined}
          rel={openInNewTab ? 'noopener noreferrer' : undefined}
          className={`${subnavLinkBaseClass} ${isActive ? 'border-b-brand-dark' : 'border-b-transparent'}`}
        >
          {label}
          {openInNewTab && <span className="sr-only"> (åbner i nyt vindue)</span>}
        </SmartLink>
      </li>
    );
  }

  return (
    <li
      className={`flex h-full items-stretch border-b-2 ${isActive ? 'border-b-brand-dark' : 'border-b-transparent'}`}
    >
      <SmartLink
        href={href}
        target={openInNewTab ? '_blank' : undefined}
        rel={openInNewTab ? 'noopener noreferrer' : undefined}
        className={navLinkBarClass}
      >
        {label}
        {openInNewTab && <span className="sr-only"> (åbner i nyt vindue)</span>}
      </SmartLink>
    </li>
  );
}

/** Visible only when count ≥ 1 (external scripts may update #jsBasketCount text). */
function BasketCountBadge() {
  const ref = useRef<HTMLSpanElement>(null);
  const [visible, setVisible] = useState(false);

  const sync = () => {
    const el = ref.current;
    if (!el) return;
    const raw = el.textContent?.trim() ?? '';
    const n = Number.parseInt(raw, 10);
    setVisible(Number.isFinite(n) && n >= 1);
  };

  useLayoutEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- sync setState in useLayoutEffect is intentional to avoid flash
    sync();
    const el = ref.current;
    if (!el) return;
    const mo = new MutationObserver(sync);
    mo.observe(el, { childList: true, characterData: true, subtree: true });
    return () => mo.disconnect();
  }, []);

  return (
    <span
      ref={ref}
      id="jsBasketCount"
      className={`shopping-basket-counter absolute right-0 top-0 flex h-[1rem] min-w-[1rem] items-center justify-center rounded-full bg-white px-1 text-[0.6875rem] font-bold leading-none text-brand-dark shadow-[0_0_0_1px_rgba(25,37,83,0.12)] transition-[background-color,color,box-shadow] duration-150 group-hover:shadow-[0_0_0_1px_rgba(25,37,83,0.2)] ${visible ? '' : 'hidden'}`}
      aria-hidden={!visible}
    >
      0
    </span>
  );
}

// ─── Basket icon and label ────────────────────────────────────────────────────
function BasketIcon() {
  return (
    <div className="group flex shrink-0 flex-col items-center lg:-mt-1.5">
      <SmartLink
        href="https://eesy.dk/bestil/"
        className="relative inline-flex cursor-pointer items-center justify-center border-0 bg-transparent p-[6px] text-brand-dark no-underline transition-colors hover:text-white group-hover:text-white rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-dark lg:p-[8px]"
        aria-label="Kurv – gå til bestilling"
      >
        <svg
          viewBox="0 0 40 40"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="block size-[1.375rem] lg:size-[1.5rem] [&_path]:fill-current"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M20 7C17.2065 7 15 9.21773 15 11.8824V16H25V11.8824C25 9.21773 22.7935 7 20 7ZM11 11.8824C11 6.94494 15.0615 3 20 3C24.9385 3 29 6.94494 29 11.8824V16H32.8359C34.673 16 36.0786 17.6362 35.8016 19.4523L34.0321 31.0553C33.5107 34.4743 30.5706 37 27.1121 37H12.8879C9.4294 37 6.4893 34.4743 5.96789 31.0553L4.19836 19.4523C3.9214 17.6362 5.32699 16 7.16407 16H11V11.8824ZM8.32814 20L9.92217 30.4523C10.1456 31.9175 11.4057 33 12.8879 33H27.1121C28.5943 33 29.8544 31.9175 30.0778 30.4523L31.6719 20H8.32814Z"
          />
        </svg>
        <BasketCountBadge />
      </SmartLink>
      <span className="block text-[0.6875rem] font-normal leading-none text-eesy-navy transition-colors group-hover:text-white lg:-mt-1">
        Kurv
      </span>
    </div>
  );
}

// ─── Profile (login) icon and label ─────────────────────────────────────────
function LoginIcon() {
  return (
    <div className="group flex shrink-0 flex-col items-center lg:-mt-1.5">
      <SmartLink
        id="loginNavBar"
        href="https://mit.eesy.dk/login"
        className="relative inline-flex cursor-pointer items-center justify-center border-0 bg-transparent p-[6px] text-brand-dark no-underline transition-colors hover:text-white group-hover:text-white rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-dark lg:p-[8px]"
        aria-label="Profil – log ind"
      >
        <svg
          viewBox="0 0 40 40"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="block size-[1.375rem] lg:size-[1.5rem] [&_path]:fill-current"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M20 7C17.2386 7 15 9.23858 15 12C15 14.7614 17.2386 17 20 17C22.7614 17 25 14.7614 25 12C25 9.23858 22.7614 7 20 7ZM11 12C11 7.02944 15.0294 3 20 3C24.9706 3 29 7.02944 29 12C29 16.9706 24.9706 21 20 21C15.0294 21 11 16.9706 11 12Z"
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M10.2091 29.1532C8.54314 30.2874 7.98833 31.6682 8.00019 32.982C8.00024 32.988 8.00029 32.994 8.00035 33H31.9936C31.9766 31.6619 31.4034 30.2781 29.7547 29.1511C28.0197 27.965 24.9834 27 19.997 27C15.0101 27 11.9541 27.9652 10.2091 29.1532ZM7.95793 25.8468C10.6193 24.0348 14.5796 23 19.997 23C25.4149 23 29.3589 24.035 32.0122 25.8489C34.7444 27.7168 35.9697 30.3257 35.9937 32.982C35.9975 33.4075 35.9993 33.7519 35.9998 34.0284C36.0031 35.6975 34.6479 37 33.0254 37H7.03296C5.34431 37 4.01044 35.6187 4.00621 33.9726C4.00555 33.7174 4.00382 33.4028 4.00035 33.018C3.97611 30.3318 5.21754 27.7126 7.95793 25.8468Z"
          />
        </svg>
      </SmartLink>
      <span className="block text-[0.6875rem] font-normal leading-none text-eesy-navy transition-colors group-hover:text-white lg:-mt-1">
        Profil
      </span>
    </div>
  );
}

// ─── Header ───────────────────────────────────────────────────────────────────
interface HeaderProps {
  fields: HeaderFields;
  appModal?: CFTextImageBlock;
}

export default function Header({ fields, appModal }: HeaderProps) {
  const [modalOpen, setModalOpen] = useState(false);
  const { menuEntries, backgroundColor, partnerLogo, partnerLogoHref, hideLoginIcon } = fields;
  const headerBg = backgroundColor ? stripEncoding(backgroundColor) : '#ffffff';

  useEffect(() => {
    const handler = () => setModalOpen(true);
    window.addEventListener('openAppModal', handler);
    return () => window.removeEventListener('openAppModal', handler);
  }, []);

  const modal = appModal ? castFields<TextImageBlockFields>(appModal as Entry<TextImageBlockFields & EntrySkeletonType>) : null;

  const menuCount = menuEntries?.length ?? 0;
  /** More than one item: show desktop + mobile nav. Zero or one: hide all header menu links. */
  const hasMultipleMenuItems = menuCount > 1;

  return (
    <header
      className="relative z-10 w-full overflow-x-clip bg-[var(--site-header-bg,#ffffff)] shadow-[0_1rem_3rem_rgba(0,0,0,0.175)]"
      style={{ '--site-header-bg': headerBg } as CSSProperties}
    >
      {/* Mobile: grid keeps actions inside the row. Desktop: flex + nav. */}
      <div className="grid min-h-[3.875rem] w-full grid-cols-[auto_minmax(0,1fr)] items-center gap-x-[8px] px-[12px] sm:px-[16px] lg:flex lg:px-[32px]">
        {/* Logo */}
        <div className="flex shrink-0 items-center gap-[8px] lg:mr-[64px] lg:gap-[12px]">
          <EesyLogo forceReload={!!partnerLogo} />
          {partnerLogo && (() => {
            const logoFields = partnerLogo.fields as { title?: string; description?: string };
            const partnerName = logoFields.description || logoFields.title || 'Partner';
            return (
              <>
                <span className="my-3 w-px self-stretch bg-brand-dark opacity-25" aria-hidden="true" />
                {partnerLogoHref ? (
                  <SmartLink href={partnerLogoHref} aria-label={partnerName} className="flex items-center">
                    <CFImage
                      asset={partnerLogo}
                      className="h-9 w-auto sm:h-10 md:h-11"
                      sizes="(max-width: 640px) 120px, 140px"
                    />
                  </SmartLink>
                ) : (
                  <span className="flex items-center">
                    <CFImage
                      asset={partnerLogo}
                      className="h-9 w-auto sm:h-10 md:h-11"
                      sizes="(max-width: 640px) 120px, 140px"
                    />
                  </span>
                )}
              </>
            );
          })()}
        </div>

        {/* Desktop nav — hidden when only one (or zero) menu items */}
        {hasMultipleMenuItems && (
          <nav className="hidden lg:flex lg:self-stretch" aria-label="Hovedmenu">
            <ul className="m-0 flex h-full min-h-[62px] list-none items-stretch gap-1 p-0">
              {menuEntries?.map((item) => {
                const f = castFields<NavigationItemFields>(item as Entry<NavigationItemFields & EntrySkeletonType>);
                return (
                  <NavLink
                    key={item.sys.id}
                    href={f.href ?? '#'}
                    label={f.label}
                    openInNewTab={f.openInNewTab}
                  />
                );
              })}
            </ul>
          </nav>
        )}

        {/* Utility actions — min-w-0 so the grid cell constrains width */}
        <div className="grid min-w-0 grid-cols-[1fr_auto_auto] items-center gap-x-[4px] lg:ml-auto lg:flex lg:shrink-0">
          <button
            type="button"
            onClick={() => setModalOpen(true)}
            className="justify-self-end min-w-0 overflow-hidden cursor-pointer whitespace-nowrap rounded-full border-0 bg-brand-dark py-2.5 px-3 text-[0.8rem] font-bold leading-[1.1] text-white transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-dark focus-visible:ring-offset-2 sm:py-3.5 sm:px-3.5"
          >
            Hent app
          </button>
          <BasketIcon />
          {!hideLoginIcon && <LoginIcon />}
        </div>
      </div>

      {/* Secondary bar: primary nav links at &lt;lg */}
      {hasMultipleMenuItems && (
        <nav
          className="block w-full overflow-x-clip lg:hidden border-t-0 bg-[var(--site-header-bg,#ffffff)] shadow-[0_-4px_18px_rgba(25,37,83,0.1)]"
          aria-label="Produktmenu"
        >
          <ul className="m-0 flex list-none flex-row flex-wrap items-stretch justify-start gap-x-[16px] gap-y-0 py-0 px-[16px] sm:px-[28px] lg:px-[24px]">
            {menuEntries?.map((item) => {
              const f = castFields<NavigationItemFields>(item as Entry<NavigationItemFields & EntrySkeletonType>);
              return (
                <NavLink
                  key={item.sys.id}
                  href={f.href ?? '#'}
                  label={f.label}
                  openInNewTab={f.openInNewTab}
                  variant="subnav"
                />
              );
            })}
          </ul>
        </nav>
      )}

      {/* App modal */}
      <Modal 
        open={modalOpen} 
        onClose={() => setModalOpen(false)} 
        variant="brand-teal"
        size="sm"
        ariaLabelledBy={modal?.header ? 'app-modal-heading' : undefined} 
        ariaLabel={!modal?.header ? 'Hent app' : undefined}
      >
        <div className="px-6 py-12 text-center">
          {modal ? (
            <>
              {modal.header && (
                <h2 id="app-modal-heading" className="mb-8 text-2xl font-bold uppercase text-brand-dark leading-tight">
                  {modal.header}
                </h2>
              )}
              {modal.image && (
                <div className="mx-auto mb-8 w-48">
                  <CFImage
                    asset={modal.image}
                    className="w-full mix-blend-multiply"
                    sizes="192px"
                  />
                </div>
              )}
              {modal.text && (
                <div className="text-sm font-medium text-brand-dark max-w-[280px] mx-auto leading-relaxed">
                  <RichText document={modal.text} />
                </div>
              )}
            </>
          ) : (
            // Fallback if no modal entry linked in Contentful yet
            <p className="text-gray-500">Indhold mangler — tilknyt en TextImageBlock i GlobalSettings → App Modal.</p>
          )}
        </div>
      </Modal>
    </header>
  );
}
